import { useMemo } from 'react';
import { FixedTermStatus, IFixedTermItem } from '../types/IFixedTermItem';

export type OrderMode = 'normal' | 'inverse' | 'estado' | 'capital';

export const useOrderList = (data?: IFixedTermItem[], orderMode: OrderMode = 'normal') => {
  return useMemo<IFixedTermItem[] | undefined>(() => {
    if (!data) return undefined;

    const isVigente = (st?: FixedTermStatus) => st === FixedTermStatus.EN_CURSO;

    const globalRank = (st?: FixedTermStatus) => {
      if (isVigente(st)) return 0;
      if (st === FixedTermStatus.DEBIN_SIN_AUTORIZAR) return 1;
      if (st === FixedTermStatus.ERROR_PLAZOFIJO) return 2;
      return 3;
    };

    // kept as-is (now it only affects "rest" bucket, which is rank 3)
    const postGlobalGroupRank = (st?: FixedTermStatus) => (isVigente(st) ? 0 : 1);

    const parseDateToTime = (s?: string): number => {
      if (!s) return 0;

      const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (m) {
        const dd = Number(m[1]);
        const mm = Number(m[2]);
        const yyyy = Number(m[3]);
        const d = new Date(yyyy, mm - 1, dd);
        const t = d.getTime();
        return Number.isNaN(t) ? 0 : t;
      }

      const t = Date.parse(s);
      return Number.isNaN(t) ? 0 : t;
    };

    const getCreatedTime = (item: IFixedTermItem) => parseDateToTime(item.createdDate);

    const getCapital = (item: IFixedTermItem) => item.amount ?? item.finalAmount ?? 0;

    // Build once: add id and keep stable index for ties
    const base = data.map((value, index) => ({
      ...value,
      id: value.certificate?.toString() ?? value.debinId ?? `row-${index}`,
      __index: index,
    }));

    // Sort indexes to avoid mutating/cloning items further if you want references preserved:
    const idxs = base.map((_, idx) => idx);

    idxs.sort((ia, ib) => {
      const A = base[ia];
      const B = base[ib];

      const g = globalRank(A.status) - globalRank(B.status);
      if (g !== 0) return g;

      if (orderMode === 'estado') {
        // For "rest" bucket: vigente group first, then group by status
        if (globalRank(A.status) === 3) {
          const vg = postGlobalGroupRank(A.status) - postGlobalGroupRank(B.status);
          if (vg !== 0) return vg;

          const sa = A.status ?? 9999;
          const sb = B.status ?? 9999;
          const sDiff = Number(sa) - Number(sb);
          if (sDiff !== 0) return sDiff;
        } else {
          // Even within global buckets, keep grouped by status
          const sa = A.status ?? 9999;
          const sb = B.status ?? 9999;
          const sDiff = Number(sa) - Number(sb);
          if (sDiff !== 0) return sDiff;
        }

        const d = getCreatedTime(B) - getCreatedTime(A);
        return d !== 0 ? d : A.__index - B.__index;
      }

      if (orderMode === 'normal') {
        // createdDate desc
        const d = getCreatedTime(B) - getCreatedTime(A);
        return d !== 0 ? d : A.__index - B.__index;
      }

      if (orderMode === 'inverse') {
        // createdDate asc
        const d = getCreatedTime(A) - getCreatedTime(B);
        return d !== 0 ? d : A.__index - B.__index;
      }

      // capital: amount desc
      const c = getCapital(B) - getCapital(A);
      if (c !== 0) return c;

      // tie-breaker: newest first
      const d = getCreatedTime(B) - getCreatedTime(A);
      return d !== 0 ? d : A.__index - B.__index;
    });

    // Return in sorted order, removing __index
    return idxs.map((i) => {
      const { __index, ...rest } = base[i];
      return rest;
    });
  }, [data, orderMode]);
};
